import React, { useState } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { Box, Button } from '@mui/material';

import {
  AutoCompleteSelectUI,
  CheckboxUI,
  DialogUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  SelectUI,
  IconPaletteUI,
} from '../Interface';
import Yup from '../../utils/yup';
import {
  getHomeScreenImageSource,
  HomeScreenLinkTypeList,
  HomeScreenLinkTypes,
  HomeScreenTileTypeList,
  HomeScreenTileTypes,
} from '../../config/homeScreen';
import { getIcon } from '../../utils/helper';

const emptyTile = {
  type: HomeScreenTileTypes.BI_REPORT,
  title: '',
  icon: '',
  biReportId: null,
  imageDataBase64: '',
  imageMimeType: '',
  imageAltText: '',
  textContent: '',
  width: 1,
  height: 1,
  visOrder: 0,
  linkType: '',
  linkTarget: '',
  openInNewTab: false,
  settings: null,
};

const validationSchema = Yup.object().shape({
  type: Yup.string().oneOf(Object.values(HomeScreenTileTypes)).required(),
  title: Yup.string().nullable(),
  biReportId: Yup.number()
    .nullable()
    .when('type', { is: HomeScreenTileTypes.BI_REPORT, then: (schema) => schema.required() }),
  imageDataBase64: Yup.string()
    .nullable()
    .when('type', {
      is: HomeScreenTileTypes.IMAGE,
      then: (schema) => schema.required('A kép kiválasztása kötelező.'),
    }),
  imageMimeType: Yup.string()
    .nullable()
    .when('type', {
      is: HomeScreenTileTypes.IMAGE,
      then: (schema) => schema.required('A kép MIME-típusa kötelező.'),
    }),
  textContent: Yup.string()
    .nullable()
    .when('type', {
      is: HomeScreenTileTypes.STATIC_TEXT,
      then: (schema) => schema.trim().required('A szöveg megadása kötelező.'),
    }),
  width: Yup.number().integer().min(1).max(6).required(),
  height: Yup.number().integer().positive().required(),
  linkType: Yup.string()
    .nullable()
    .test('link-pair', 'A hivatkozás típusa és célja csak együtt adható meg.', function (value) {
      return Boolean(value) === Boolean(this.parent.linkTarget);
    }),
  linkTarget: Yup.string()
    .nullable()
    .test('link-pair', 'A hivatkozás típusa és célja csak együtt adható meg.', function (value) {
      return Boolean(value) === Boolean(this.parent.linkType);
    })
    .test('link-format', 'A hivatkozás formátuma nem megfelelő.', function (value) {
      if (!value) return true;
      if (this.parent.linkType === HomeScreenLinkTypes.INTERNAL) return value.startsWith('/');
      if (this.parent.linkType === HomeScreenLinkTypes.EXTERNAL) return /^https?:\/\//i.test(value);
      return false;
    }),
});

const readImage = (file, setFieldValue) => {
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    setFieldValue('imageDataBase64', String(reader.result));
    setFieldValue('imageMimeType', file.type);
  };
  reader.readAsDataURL(file);
};

const HomeScreenIconPicker = () => {
  const { values, setFieldValue } = useFormikContext();
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverClose = () => setAnchorEl(null);

  const handleIconSelect = (iconName) => {
    setFieldValue('icon', iconName);
    handlePopoverClose();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        type="button"
        variant="outlined"
        startIcon={values.icon ? getIcon(values.icon, '1.5rem') : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Ikon kiválasztása
      </Button>
      {values.icon && (
        <Button type="button" color="inherit" onClick={() => setFieldValue('icon', '')}>
          Törlés
        </Button>
      )}
      <IconPaletteUI handleIconSelect={handleIconSelect} handlePopoverClose={handlePopoverClose} anchorEl={anchorEl} />
    </Box>
  );
};

const HomeScreenTileDialog = ({ open, onClose, onSave, tile, biReports }) => (
  <DialogUI
    open={open}
    onClose={onClose}
    headerContent={tile ? 'Csempe szerkesztése' : 'Csempe hozzáadása'}
    fullWidth
    maxWidth="md"
  >
    <Formik
      initialValues={{
        ...emptyTile,
        ...(tile ?? {}),
        linkType: tile?.linkType ?? '',
        linkTarget: tile?.linkTarget ?? '',
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize
      onSubmit={onSave}
    >
      {({ values, errors, isSubmitting, setFieldValue, submitForm }) => (
        <Form>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              py: 1.5,
            }}
          >
            <Box>
              <Field
                name="type"
                component={SelectUI}
                option={HomeScreenTileTypeList}
                value={values.type}
                label={<FormLabelUI text="Csempetípus" />}
                helperText={<FormErrorUI message={errors.type} />}
                onChange={(event) => setFieldValue('type', event.target.value)}
              />
            </Box>
            <Box>
              <Field
                name="title"
                as={InputUI}
                fullWidth
                variant="standard"
                label={<FormLabelUI text="Cím" />}
                helperText={<FormErrorUI message={errors.title} />}
              />
            </Box>
            {values.type === HomeScreenTileTypes.BI_REPORT && (
              <Box sx={{ gridColumn: { md: 'span 2' } }}>
                <Field
                  name="biReportId"
                  component={AutoCompleteSelectUI}
                  fullWidth
                  label={<FormLabelUI text="BI riport" />}
                  variant="standard"
                  dataset={biReports}
                  selectedValue={values.biReportId}
                  selectedLabelValue={biReports.find((item) => item.value === values.biReportId)?.label ?? ''}
                  onChange={(_event, value) => setFieldValue('biReportId', value?.value ?? null)}
                  helperText={<FormErrorUI message={errors.biReportId} />}
                />
              </Box>
            )}

            {values.type === HomeScreenTileTypes.IMAGE && (
              <>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <Box sx={{ mb: 1 }}>
                    <FormLabelUI text="Kép" />
                  </Box>
                  <Button component="label" variant="outlined">
                    Kép kiválasztása
                    <Box
                      component="input"
                      sx={{ display: 'none' }}
                      type="file"
                      accept="image/*"
                      onChange={(event) => readImage(event.target.files?.[0], setFieldValue)}
                    />
                  </Button>
                  <FormErrorUI message={errors.imageDataBase64 || errors.imageMimeType} />
                  {values.imageDataBase64 && (
                    <Box
                      component="img"
                      sx={{
                        mt: 1.5,
                        width: '100%',
                        maxHeight: 192,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                        objectFit: 'contain',
                        p: 1,
                      }}
                      src={getHomeScreenImageSource(values)}
                      alt={values.imageAltText || 'Kép előnézete'}
                    />
                  )}
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <Field
                    name="imageAltText"
                    as={InputUI}
                    fullWidth
                    variant="standard"
                    label={<FormLabelUI text="Alternatív képszöveg" />}
                  />
                </Box>
              </>
            )}

            {values.type === HomeScreenTileTypes.STATIC_TEXT && (
              <>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <HomeScreenIconPicker />
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <Field
                    name="textContent"
                    as={InputUI}
                    fullWidth
                    multiline
                    minRows={5}
                    variant="outlined"
                    label={<FormLabelUI text="Statikus szöveg" />}
                    helperText={<FormErrorUI message={errors.textContent} />}
                  />
                </Box>
              </>
            )}

            <Box>
              <Field
                name="width"
                type="number"
                as={InputUI}
                fullWidth
                variant="standard"
                inputProps={{ min: 1, max: 6 }}
                label={<FormLabelUI text="Szélesség (1–6)" />}
                helperText={<FormErrorUI message={errors.width} />}
              />
            </Box>
            <Box>
              <Field
                name="height"
                type="number"
                as={InputUI}
                fullWidth
                variant="standard"
                inputProps={{ min: 1 }}
                label={<FormLabelUI text="Magasság" />}
                helperText={<FormErrorUI message={errors.height} />}
              />
            </Box>
            <Box>
              <Field
                name="linkType"
                component={SelectUI}
                option={HomeScreenLinkTypeList}
                value={values.linkType ?? ''}
                label={<FormLabelUI text="Hivatkozás típusa" />}
                onChange={(event) => setFieldValue('linkType', event.target.value)}
                onClear={() => setFieldValue('linkType', '')}
                helperText={<FormErrorUI message={errors.linkType} />}
              />
            </Box>
            <Box>
              <Field
                name="linkTarget"
                as={InputUI}
                fullWidth
                variant="standard"
                label={<FormLabelUI text="Hivatkozás célja" />}
                helperText={<FormErrorUI message={errors.linkTarget} />}
              />
            </Box>
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <CheckboxUI
                name="openInNewTab"
                checked={Boolean(values.openInNewTab)}
                onCheckedChange={(checked) => setFieldValue('openInNewTab', checked)}
                label={<FormLabelUI text="Megnyitás új lapon" />}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, py: 1 }}>
            <Button type="button" variant="contained" disabled={isSubmitting} onClick={submitForm}>
              Rögzítés
            </Button>
            <Button type="button" variant="outlined" disabled={isSubmitting} onClick={onClose}>
              Mégse
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  </DialogUI>
);

export default HomeScreenTileDialog;
